import { InlineSelect } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import { guardArray } from "@msuite/utils/array";
import type { ReturnOrderFormSchema } from "@msuite/utils/schemas";
import { useFormContext } from "react-hook-form";
import { useVehicles } from "@/hooks/use-vehicles";

/**
 * The trucks fetching the material, picked from the organisation's own fleet.
 *
 * Several may be chosen, because clearing a site takes more than one lorry, and none is a valid
 * answer — the office often raises the run before deciding which are going, and the sheet prints
 * either way, saying "Noch nicht zugeordnet". Hence `isRequired={false}`, which matches
 * `vehicleIds` being optional on the schema.
 *
 * The context is typed to the one field rather than to a whole form, because two forms mount this:
 * raising a run, and editing the fleet of one already filed. Both carry `vehicleIds` and nothing
 * else here reads from either.
 */
export function ReturnOrderFormVehicles() {
	/** Context */
	const methods = useFormContext<Pick<ReturnOrderFormSchema, "vehicleIds">>();

	/** Hooks */
	const { t } = useTranslation();

	/** Query */
	const { vehicles } = useVehicles();

	/** Render */
	return (
		<InlineSelect
			isRequired={false}
			label={t("stock.return_vehicles")}
			control={methods.control}
			name="vehicleIds"
			options={guardArray(vehicles).map((vehicle) => ({
				label: vehicle.licensePlate,
				value: vehicle.id,
			}))}
		/>
	);
}
